import { useEffect, useState, useMemo, useCallback } from "preact/hooks";
import { com } from "../../proto/game.pb";
import { useWebSocket } from "../../shared/contexts";
import { MT } from "../../shared/types/proto/mt";

export interface FreebetInfo {
  freebetId: string;
  amount: number;
  expiresAt: number;
  source: string;
  targetSlotId: number;
}

// Progressive freebet v2: прогресс ведётся per-user+slot, порог сейчас всегда 3 hit.
export interface FreebetProgressState {
  currentProgress: number;
  requiredForFreebet: number;
  trackedAmount: number;
  slotId: number;
}

type FreebetProgressBySlot = Record<number, FreebetProgressState>;
type EligibilityBySlot = Record<number, boolean>;

// Порог выдачи фрибета (см. progressive_freebet_frontend_integration_ru.md, сейчас всегда 3).
export const FREEBET_PROGRESS_REQUIRED = 3;

const createDefaultProgress = (slotId: number): FreebetProgressState => ({
  currentProgress: 0,
  requiredForFreebet: FREEBET_PROGRESS_REQUIRED,
  trackedAmount: 0,
  slotId,
});

const parseFreebetInfo = (
  fb: com.achlid.crash.game.stateless.IFreebetInfo
): FreebetInfo => ({
  freebetId: fb.freebetId || "",
  amount: Number(fb.amount || 0),
  expiresAt: Number(fb.expiresAt || 0),
  source: fb.source || "",
  targetSlotId: Number(fb.targetSlotId || 0),
});

const parseFreebetNotification = (
  notif: com.achlid.crash.game.IFreebetGrantedNotification
): FreebetInfo => ({
  freebetId: notif.freebetId || "",
  amount: Number(notif.amount || 0),
  expiresAt: Number(notif.expiresAt || 0),
  source: notif.reason || "PROGRESSIVE_INSTA_CRASH",
  targetSlotId: Number(notif.targetSlotId || 0),
});

const parseFreebetProgress = (
  update: com.achlid.crash.game.IFreebetProgressUpdate
): FreebetProgressState => ({
  currentProgress: Number(update.currentProgress || 0),
  requiredForFreebet: Number(
    update.requiredForFreebet || FREEBET_PROGRESS_REQUIRED
  ),
  trackedAmount: Number(update.trackedAmount || 0),
  slotId: Number(update.slotId || 0),
});

export function useFreebets(slotId: number) {
  const { sendProto, onMessage, status } = useWebSocket();

  const [allFreebets, setAllFreebets] = useState<FreebetInfo[]>([]);
  const [progressBySlot, setProgressBySlot] = useState<FreebetProgressBySlot>({
    1: createDefaultProgress(1),
    2: createDefaultProgress(2),
  });
  // Персональный сигнал из STARTING: участвует ли слот в прогрессе в этом раунде.
  const [eligibilityBySlot, setEligibilityBySlot] = useState<EligibilityBySlot>(
    {
      1: false,
      2: false,
    }
  );
  // Был ли использован фрибет в этом раунде именно на этом слоте.
  const [freebetUsedThisRound, setFreebetUsedThisRound] = useState(false);

  const removeFreebetForSlot = useCallback((targetSlotId: number) => {
    setAllFreebets((prev) => {
      const idx = prev.findIndex(
        (fb) => fb.targetSlotId === targetSlotId || fb.targetSlotId === 0
      );
      return idx >= 0 ? [...prev.slice(0, idx), ...prev.slice(idx + 1)] : prev;
    });
  }, []);

  useEffect(() => {
    if (status !== "open") return;
    sendProto({ type: MT.GET_FREEBETS, timestamp: Date.now() });
  }, [status, sendProto]);

  useEffect(() => {
    return onMessage(MT.FREEBETS_RESPONSE, (msg) => {
      const res = msg.freebetsResponse;
      if (!res?.freebets) return;

      const list = res.freebets.map(parseFreebetInfo);

      setAllFreebets(list);
    });
  }, [onMessage]);

  // Eligibility приходит в STARTING: показываем "ставка участвует в прогрессе".
  useEffect(() => {
    return onMessage(MT.FREEBET_PROGRESS_ELIGIBILITY, (msg) => {
      const eligibility = msg.freebetProgressEligibility;
      if (!eligibility) return;

      setEligibilityBySlot({
        1: !!eligibility.slot1Eligible,
        2: !!eligibility.slot2Eligible,
      });
    });
  }, [onMessage]);

  // Фактическое изменение прогресса — только по FREEBET_PROGRESS_UPDATE (server-authoritative).
  useEffect(() => {
    return onMessage(MT.FREEBET_PROGRESS_UPDATE, (msg) => {
      const update = msg.freebetProgressUpdate;
      if (!update) return;

      const state = parseFreebetProgress(update);
      if (state.slotId !== 1 && state.slotId !== 2) return;

      setProgressBySlot((prev) => ({ ...prev, [state.slotId]: state }));
    });
  }, [onMessage]);

  useEffect(() => {
    return onMessage(MT.FREEBET_GRANTED_NOTIFICATION, (msg) => {
      const notif = msg.freebetGrantedNotification;
      if (!notif) return;

      const freebet = parseFreebetNotification(notif);

      setAllFreebets((prev) => {
        // Проверка на дубликат (по targetSlotId, amount и expiresAt)
        const isDuplicate = prev.some(
          (fb) =>
            fb.targetSlotId === freebet.targetSlotId &&
            fb.amount === freebet.amount &&
            fb.expiresAt === freebet.expiresAt
        );
        if (isDuplicate) return prev;
        return [...prev, freebet];
      });
      // Прогресс НЕ сбрасываем локально — сервер присылает current_progress=0
      // в соответствующем FREEBET_PROGRESS_UPDATE (freebet_granted=true).
    });
  }, [onMessage]);

  useEffect(() => {
    const offBetResult = onMessage(MT.BET_RESULT, (msg) => {
      // Реагируем только на фрибет-ставку СВОЕГО слота
      if (msg.betResult?.isFreebet && Number(msg.betResult.slotId) === slotId) {
        setFreebetUsedThisRound(true);
      }
    });

    const offRoundCrash = onMessage(MT.ROUND_CRASH, (msg) => {
      if (!msg.roundCrash) return;
      // Новый раунд — eligibility сбрасываем до следующего STARTING-сигнала
      setEligibilityBySlot({ 1: false, 2: false });

      if (!freebetUsedThisRound) return;
      removeFreebetForSlot(slotId);
      setFreebetUsedThisRound(false);
    });

    return () => {
      offBetResult();
      offRoundCrash();
    };
  }, [onMessage, slotId, freebetUsedThisRound, removeFreebetForSlot]);

  const freebets = useMemo(
    () =>
      allFreebets.filter(
        (fb) => fb.targetSlotId === 0 || fb.targetSlotId === slotId
      ),
    [allFreebets, slotId]
  );

  const freebetProgress = useMemo(
    () => progressBySlot[slotId] ?? createDefaultProgress(slotId),
    [progressBySlot, slotId]
  );

  const isEligible = useMemo(
    () => !!eligibilityBySlot[slotId],
    [eligibilityBySlot, slotId]
  );

  return { freebets, freebetProgress, isEligible, allFreebets, progressBySlot };
}
